import {memo} from 'react'

import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import IconWavyCheckSolid from '@adv-mt/mt-svg-icons/lib/WavyCheckSolid.js'
import {Icon, ICON_INTENTS, ICON_SIZES} from '@adv-mt/ui/icon'
import {Headline1} from '@adv-mt/ui/typography'

import Separator, {SEPARATOR_SIZES} from '../../components/separator/Separator.js'
import {BASE_CLASS as PAGE_BASE_CLASS} from '../../config.js'
import Carfax from './carfax/index.js'
import Certification from './certification/index.js'
import NoCommissions from './noCommissions/index.js'

const BASE_CLASS = `${PAGE_BASE_CLASS}-trust`

const Trust = ({ad = {}, adType = {}, onOpenDrawer, vehicleCertification = {}, vehicleHistory}) => {
  const {domain, i18n} = useSuiContext()

  const config = domain.get('config')
  const trustSectionId = config.get('COMMON').AD_DETAIL_PAGE_SECTIONS.AD_DETAIL_PAGE_SECTION_IDS.TRUST
  const {
    SERVICE_PROVIDERS: {CARFAX}
  } = config.get('COMMON')
  const {AVAILABLE_SERVICE_PROVIDERS} = config.get('serviceProviders')
  const {OFFER_TYPE_NEW} = config.get('search')

  const {HAS_AD_DETAIL_TRUST_CERTIFICATION_ENABLED, HAS_AD_DETAIL_TRUST_CARFAX_ENABLED} = config.get('features')

  const hasCertification = HAS_AD_DETAIL_TRUST_CERTIFICATION_ENABLED && vehicleCertification.features?.length > 0

  const isSubscriptionAd = Boolean(ad.monthlySubscriptionFee)
  const isNewVehicle = adType.offerTypeId === OFFER_TYPE_NEW
  const hasCarfax =
    HAS_AD_DETAIL_TRUST_CARFAX_ENABLED &&
    Boolean(AVAILABLE_SERVICE_PROVIDERS[CARFAX]) &&
    !isSubscriptionAd &&
    !isNewVehicle &&
    Boolean(vehicleHistory?.hasReport)

  return (
    <section id={trustSectionId} className={BASE_CLASS}>
      <div className={`${BASE_CLASS}-header`}>
        <Headline1>{i18n.t('COMMON.DETAIL_TRUST_TITLE')}</Headline1>
        <Icon intent={ICON_INTENTS.SUCCESS} size={ICON_SIZES.MD}>
          <IconWavyCheckSolid color={ICON_INTENTS.SUCCESS} size={ICON_SIZES.MD} />
        </Icon>
      </div>

      <div className={`${BASE_CLASS}-content`}>
        {hasCertification && (
          <>
            <Certification onOpenDrawer={onOpenDrawer} vehicleCertification={vehicleCertification} />
            <Separator size={SEPARATOR_SIZES.MEDIUM} />
          </>
        )}

        {hasCarfax && (
          <>
            <Carfax adId={ad.id} onOpenDrawer={onOpenDrawer} vehicleHistory={vehicleHistory} />
            <Separator size={SEPARATOR_SIZES.MEDIUM} />
          </>
        )}

        <NoCommissions onOpenDrawer={onOpenDrawer} />
      </div>
    </section>
  )
}

Trust.displayName = 'Trust'

Trust.propTypes = {
  ad: PropTypes.shape({
    id: PropTypes.string,
    monthlySubscriptionFee: PropTypes.number
  }),
  adType: PropTypes.shape({
    offerTypeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  onOpenDrawer: PropTypes.func.isRequired,
  vehicleCertification: PropTypes.shape({
    features: PropTypes.arrayOf(
      PropTypes.shape({
        description: PropTypes.string,
        title: PropTypes.string
      })
    ),
    logoUrl: PropTypes.string,
    make: PropTypes.string
  }),
  vehicleHistory: PropTypes.shape({
    hasReport: PropTypes.bool,
    reportUrl: PropTypes.string,
    verifiedData: PropTypes.shape({
      value: PropTypes.arrayOf(PropTypes.shape({text: PropTypes.string, value: PropTypes.string}))
    })
  })
}

export default memo(Trust)
